import React, { useEffect, useState } from "react";
import { api, formatCurrency } from "@/lib/api";
import { logger } from "@/lib/logger";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, ArrowDownRight, CalendarClock, Wallet, Sparkles, ChevronRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AccountTxSheet from "@/components/AccountTxSheet";
import MobileWatchlistCards from "@/components/MobileWatchlistCards";

// Shared helpers so the "days until due" mapping lives in one place instead of
// inline nested ternaries scattered around the layout.
const dueToneColor = (days) => {
  if (days == null) return "text-[#5F7368]";
  if (days <= 2) return "text-[#D95D39]";
  if (days <= 7) return "text-[#C89A3F]";
  return "text-[#1C7C54]";
};
const dueLabel = (days) => {
  if (days === 0) return "hoy";
  if (days === 1) return "mañana";
  return `en ${days} días`;
};

const CATEGORY_ICONS = {
  nomina: "💼",
  hipoteca: "🏠",
  alquiler: "🏠",
  servicios: "💡",
  supermercado: "🛒",
  restaurantes: "🍽️",
  transporte: "🚗",
  gasolina: "⛽",
  salud: "🏥",
  ocio: "🎉",
  otros: "•",
};

/**
 * Compact home cards for mobile only. Rendered inside Dashboard behind a
 * `md:hidden` wrapper so desktop is untouched. Uses a dedicated small API
 * endpoint to keep the payload minimal on cellular connections.
 */
export default function MobileHome() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sheetAcc, setSheetAcc] = useState(null);
  const cur = user?.base_currency || "USD";

  useEffect(() => {
    let cancelled = false;
    api.get("/dashboard/mobile-home")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => logger.error("mobile-home", e))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="md:hidden space-y-3" data-testid="mobile-home-loading">
        <div className="h-32 rounded-2xl bg-[#EBEBEA]/60 animate-pulse" />
        <div className="h-24 rounded-2xl bg-[#EBEBEA]/60 animate-pulse" />
        <div className="h-24 rounded-2xl bg-[#EBEBEA]/60 animate-pulse" />
      </div>
    );
  }
  if (!data) return null;

  const positiveNet = (data.net_worth || 0) >= 0;
  const nd = data.next_due;
  const tx = data.last_tx;
  const txPositive = tx && tx.amount >= 0;
  const dueTone = dueToneColor(nd?.days);

  return (
    <div className="md:hidden flex flex-col gap-3 fade-up" data-testid="mobile-home">
      {/* Net worth hero */}
      <button
        type="button"
        onClick={() => nav("/dashboard#full")}
        data-testid="mobile-net-worth-card"
        className="text-left card-surface p-6 active:scale-[0.99] transition-transform"
      >
        <div className="flex items-center gap-2 text-[#8A9992]">
          <Wallet className="w-4 h-4" />
          <span className="text-[10px] uppercase tracking-[0.14em] font-bold">Patrimonio neto</span>
        </div>
        <div className={`font-display text-4xl font-semibold tabular-nums mt-1 ${positiveNet ? "text-[#0A110E]" : "text-[#D95D39]"}`}>
          {formatCurrency(data.net_worth, data.base_currency || cur)}
        </div>
        <div className="text-xs text-[#8A9992] mt-1">Toca para ver el panel completo</div>
      </button>

      {/* Watchlist cards */}
      <MobileWatchlistCards />

      {/* Next due */}
      <button
        type="button"
        onClick={() => nav("/debts")}
        data-testid="mobile-next-due-card"
        className="text-left card-surface p-5 active:scale-[0.99] transition-transform"
      >
        <div className="flex items-center gap-2 text-[#8A9992]">
          <CalendarClock className="w-4 h-4" />
          <span className="text-[10px] uppercase tracking-[0.14em] font-bold">Próximo vencimiento</span>
        </div>
        {nd ? (
          <>
            <div className="flex items-baseline gap-3 mt-1">
              <div className="font-display text-2xl font-semibold text-[#0A110E]">
                {formatCurrency(nd.amount, nd.currency)}
              </div>
              <div className={`text-sm font-semibold ${dueTone}`}>
                {dueLabel(nd.days)}
              </div>
            </div>
            <div className="text-sm text-[#5F7368] mt-0.5 truncate">
              {nd.is_mortgage ? "🏠 " : ""}{nd.creditor}
            </div>
          </>
        ) : (
          <div className="text-sm text-[#5F7368] mt-2">Sin cuotas próximas 🎉</div>
        )}
      </button>

      {/* Last transaction */}
      <button
        type="button"
        onClick={() => nav("/accounts")}
        data-testid="mobile-last-tx-card"
        className="text-left card-surface p-5 active:scale-[0.99] transition-transform"
      >
        <div className="flex items-center gap-2 text-[#8A9992]">
          {txPositive ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
          <span className="text-[10px] uppercase tracking-[0.14em] font-bold">Último movimiento</span>
        </div>
        {tx ? (
          <>
            <div className="flex items-baseline gap-3 mt-1">
              <span className="text-2xl">{CATEGORY_ICONS[tx.category] || "•"}</span>
              <div className={`font-display text-2xl font-semibold tabular-nums ${txPositive ? "text-[#1C7C54]" : "text-[#0A110E]"}`}>
                {txPositive ? "+" : ""}{formatCurrency(tx.amount, tx.currency)}
              </div>
            </div>
            <div className="text-sm text-[#5F7368] mt-0.5 truncate">
              {tx.description || tx.category}
              {tx.account_alias ? ` · ${tx.account_alias}` : ""}
            </div>
            <div className="text-xs text-[#8A9992] mt-0.5">{tx.date}</div>
          </>
        ) : (
          <div className="text-sm text-[#5F7368] mt-2">
            Importa un extracto para ver tus movimientos
          </div>
        )}
      </button>

      {/* Upcoming due dates list */}
      {Array.isArray(data.upcoming) && data.upcoming.length > 1 && (
        <button
          type="button"
          onClick={() => nav("/debts")}
          data-testid="mobile-upcoming-card"
          className="text-left card-surface p-5 active:scale-[0.99] transition-transform"
        >
          <div className="flex items-center gap-2 text-[#8A9992]">
            <CalendarClock className="w-4 h-4" />
            <span className="text-[10px] uppercase tracking-[0.14em] font-bold">Próximos vencimientos</span>
          </div>
          <div className="mt-2">
            {(() => {
              let lastMonthKey = null;
              const monthNames = ["ene","feb","mar","abr","may","jun","jul","ago","sep","oct","nov","dic"];
              return data.upcoming.map((u, i) => {
                const d = new Date(u.date);
                const mk = `${d.getFullYear()}-${d.getMonth()}`;
                const showHeader = mk !== lastMonthKey;
                lastMonthKey = mk;
                const monthLabel = `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
                const tone = dueToneColor(u.days);
                // Stable per-item key: creditor + due date + amount.
                const rowKey = `${u.creditor}-${u.date}-${u.amount}`;
                return (
                  <React.Fragment key={rowKey}>
                    {showHeader && (
                      <div
                        data-testid={`mobile-upcoming-month-${mk}`}
                        className="text-[10px] uppercase tracking-[0.14em] text-[#8A9992] font-bold pt-2 pb-1"
                      >
                        {monthLabel}
                      </div>
                    )}
                    <div data-testid={`mobile-upcoming-row-${i}`} className="py-2 flex items-center gap-3 border-t border-[#EBEBEA] first:border-t-0">
                      <span className="text-lg">{u.is_mortgage ? "🏠" : "💳"}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-[#0A110E] truncate">{u.creditor}</div>
                        <div className={`text-xs ${tone}`}>
                          {dueLabel(u.days)} · {u.date}
                        </div>
                      </div>
                      <div className="text-sm font-semibold tabular-nums text-[#0A110E]">
                        {formatCurrency(u.amount, u.currency)}
                      </div>
                    </div>
                  </React.Fragment>
                );
              });
            })()}
          </div>
        </button>
      )}

      {/* Accounts quick access */}
      {Array.isArray(data.accounts) && data.accounts.length > 0 && (
        <div className="card-surface p-5" data-testid="mobile-accounts-card">
          <div className="flex items-center gap-2 text-[#8A9992] mb-2">
            <Wallet className="w-4 h-4" />
            <span className="text-[10px] uppercase tracking-[0.14em] font-bold">Tus cuentas</span>
          </div>
          <div className="divide-y divide-[#EBEBEA] -mx-2">
            {data.accounts.map((a) => (
              <button
                type="button"
                key={a.id}
                onClick={() => setSheetAcc(a)}
                data-testid={`mobile-account-${a.id}`}
                className="w-full flex items-center gap-3 px-2 py-3 active:bg-[#EBEBEA]/40 rounded-lg transition-colors"
              >
                <div className="w-9 h-9 rounded-lg bg-[#1C7C54]/10 flex items-center justify-center shrink-0">
                  <Wallet className="w-4 h-4 text-[#1C7C54]" />
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <div className="text-sm font-medium text-[#0A110E] truncate">
                    {a.alias || a.bank}
                  </div>
                  <div className="text-[11px] text-[#8A9992] truncate">
                    {a.bank}{a.type ? ` · ${a.type}` : ""}
                  </div>
                </div>
                <div className="text-sm font-semibold tabular-nums text-[#0A110E]">
                  {formatCurrency(a.balance, a.currency)}
                </div>
                <ChevronRight className="w-4 h-4 text-[#8A9992] shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Ask AI quick pill */}
      <button
        type="button"
        onClick={() => nav("/assistant")}
        data-testid="mobile-ask-ai-card"
        className="flex items-center gap-3 rounded-2xl border border-[#1C7C54]/30 bg-[#1C7C54]/5 px-4 py-3 text-left active:scale-[0.99] transition-transform"
      >
        <Sparkles className="w-5 h-5 text-[#1C7C54]" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-[#0A110E]">Pregunta al asistente</div>
          <div className="text-xs text-[#5F7368]">¿En qué gasté más? ¿Amortizar hipoteca?</div>
        </div>
      </button>
      <AccountTxSheet
        open={!!sheetAcc}
        onOpenChange={(v) => { if (!v) setSheetAcc(null); }}
        account={sheetAcc}
      />
    </div>
  );
}
